/**
 * zeq-contract.ts — the Pulse's bind / verify / history surface.
 *
 * Phase 3 (per Zeq Orb v1 §5.3 + HulyaPulse §5.B). Each call wraps the
 * existing kernel HTTP surface — NO new server endpoints. The architectural
 * rule the user locked in: "each state contract is its own agent."
 *
 *   ZeqContract.bind   → ./sdk/zeq/compute  (the slug-host SDK proxy
 *                        attaches the per-slug publish key, no key on
 *                        the page)
 *   ZeqContract.verify → ./sdk/zeq/verify
 *   ZeqContract.history → ./sdk/zeq/history (chain rows for this slug)
 *
 * Every bind() drives the orb through:
 *
 *   idle → thinking → executing → settling → confirmed   (success)
 *   idle → thinking → executing → rejected               (server 4xx/5xx)
 *   idle → thinking → executing → offline                (network down)
 *
 * Settling auto-reverts to confirmed; confirmed/rejected auto-revert to
 * idle (logic lives in orb-element.ts).
 */

import { setState as setOrbState } from "../orb/orb-element.js";
import type { OrbState } from "../orb/orb-element.js";
import { ZeqondClock, T_Z_SECONDS } from "../orb/zeqond-clock.js";
import { signWithDeviceKey, getDeviceKeyId } from "./device-key.js";

// ─── Types ─────────────────────────────────────────────────────────────

export type ContractExecuteAt = "now" | "next" | number;

export interface ContractBindArgs {
  /** Operator id from the 1,500+-operator catalogue, e.g. "NM19", "GR33". */
  contractId: string;
  /** Numeric inputs the operator expects. */
  inputs: Record<string, number>;
  /**
   * "now" — fire immediately.
   * "next" — wait for the next Zeqond boundary, then fire (default).
   * <number> — specific Zeqond to fire at (must be in the future).
   */
  executeAt?: ContractExecuteAt;
  /**
   * Optional domain hint to help the wizard pick the right operator
   * family. If omitted the kernel infers from the contractId.
   */
  domain?: string;
  /**
   * If true, the bind will publish the result to the public ZeqState feed
   * for this slug. Default: false (private to the owner).
   */
  publish?: boolean;
}

export interface ContractResult {
  /** Numeric value the operator produced. */
  value: number;
  /** Unit string ("J", "m/s", "Hz", etc.). */
  unit: string;
  /** Reported precision bound (typically ≤ 0.001 = 0.1%). */
  uncertainty: number;
  /** ZeqProof — HMAC-SHA256 binding the result to the operator chain. */
  zeqProof: string;
  /** Integer Zeqond at which the result was settled. */
  zeqond: number;
  /** Phase ∈ [0, 1) inside that Zeqond. */
  phase: number;
  /**
   * Operator chain that ran (KO42 always present at index 0).
   */
  operatorChain: string[];
  /** R(t) — universal modulation. */
  R_t?: number;
  /**
   * Status:
   *   "confirmed"   — server-issued canonical ZeqProof.
   *   "provisional" — locally signed by the device (Off-Pulse). Will
   *                    flip to confirmed/rejected after Resync.
   *   "rejected"    — kernel returned an error or KO42 guard failed.
   */
  status: "confirmed" | "provisional" | "rejected";
  /** Server transition id (if confirmed). */
  transitionId?: string;
  /** Why it was rejected (if status = "rejected"). */
  reason?: string;
}

// ─── HTTP base resolution ──────────────────────────────────────────────

/**
 * Pick the right base URL for the ./sdk/zeq/* proxy.
 *
 *   - On a hosted slug page (URL is /s/<slug>/...) we resolve relative to
 *     the current pathname so the proxy gets called.
 *   - Off a slug page (e.g. /pulse/test/sandbox.html) we fall back to the
 *     direct kernel endpoints. Requires the page or the test environment
 *     to provide some auth (cookie, Bearer key, or owner state machine).
 */
function getSdkBase(): { kind: "slug" | "kernel"; base: string } {
  if (typeof location === "undefined") return { kind: "kernel", base: "/api/zeq" };
  const m = /^\/s\/([^/]+)\//.exec(location.pathname);
  if (m) return { kind: "slug", base: `/s/${m[1]}/sdk/zeq` };
  return { kind: "kernel", base: "/api/zeq" };
}

// ─── Schedule a future Zeqond ──────────────────────────────────────────

function msUntilZeqond(target: number): number {
  const T_Z_MS = T_Z_SECONDS * 1000;
  const nowZ = ZeqondClock.now().zeqond;
  if (target <= nowZ) return 0;
  // Simple linear delay — re-derive each call so we don't fight wall-clock drift.
  return Math.max(0, (target - nowZ) * T_Z_MS - (Date.now() % T_Z_MS));
}

async function delay(ms: number): Promise<void> {
  if (ms <= 0) return;
  await new Promise((r) => setTimeout(r, ms));
}

// ─── bind() ────────────────────────────────────────────────────────────

export async function bind(args: ContractBindArgs): Promise<ContractResult> {
  // Compose the orb's "thinking" affordance even before the network call so
  // the pulse reflects "I'm starting work."
  setOrbState("thinking");

  // executeAt scheduling.
  if (args.executeAt === "next" || args.executeAt === undefined) {
    const T_Z_MS = T_Z_SECONDS * 1000;
    await delay(T_Z_MS - (Date.now() % T_Z_MS));
  } else if (typeof args.executeAt === "number") {
    await delay(msUntilZeqond(args.executeAt));
  }

  setOrbState("executing");

  const { kind, base } = getSdkBase();
  const url = `${base}/compute`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        operators: [args.contractId],
        inputs: args.inputs,
        ...(args.domain ? { domain: args.domain } : {}),
        ...(args.publish ? { publish: true } : {}),
      }),
    });
  } catch (err) {
    // Network failure → Off-Pulse, return a provisional result if possible.
    setOrbState("offline");
    return await buildProvisionalResult(args, "network_unreachable");
  }

  if (!response.ok) {
    setOrbState("rejected");
    let reason = `HTTP ${response.status}`;
    try {
      const errBody = await response.json();
      reason = errBody?.error || errBody?.code || reason;
    } catch { /* ignore */ }
    return {
      value: NaN,
      unit: "",
      uncertainty: NaN,
      zeqProof: "",
      zeqond: ZeqondClock.now().zeqond,
      phase: ZeqondClock.now().phase,
      operatorChain: [args.contractId],
      status: "rejected",
      reason,
    };
  }

  const data = (await response.json()) as Record<string, unknown>;
  const result = parseComputeResponse(data, args.contractId);

  // settling → confirmed → idle (auto-revert chain in orb-element.ts).
  setOrbState("settling");
  return result;
}

function parseComputeResponse(data: Record<string, unknown>, fallbackContractId: string): ContractResult {
  // The kernel envelope shape (see shared/api-core/src/routes/zeq.ts):
  //   { ...wizardResult, equations, master_sum, compliance, cko, zeqProof, _kernelUrl }
  // wizardResult.zeqState carries operators, zeqond, phase, precision; result carries R_t/S_t.
  const zeqState = (data.zeqState ?? {}) as Record<string, unknown>;
  const result = (data.result ?? {}) as Record<string, unknown>;
  const value = typeof data.value === "number" ? (data.value as number) : NaN;
  const unit = typeof data.unit === "string" ? (data.unit as string) : "";
  const operators = Array.isArray(zeqState.operators) ? (zeqState.operators as string[]) : [fallbackContractId];
  return {
    value,
    unit,
    uncertainty: typeof zeqState.precision === "number" ? (zeqState.precision as number) : 0.001,
    zeqProof: typeof data.zeqProof === "string" ? (data.zeqProof as string) : "",
    zeqond: typeof zeqState.zeqond === "number" ? (zeqState.zeqond as number) : ZeqondClock.now().zeqond,
    phase: typeof zeqState.phase === "number" ? (zeqState.phase as number) : ZeqondClock.now().phase,
    operatorChain: operators,
    R_t: typeof result.R_t === "number" ? (result.R_t as number) : undefined,
    status: "confirmed",
    transitionId:
      typeof (data.compliance as Record<string, unknown>)?.envelope_id === "string"
        ? ((data.compliance as Record<string, unknown>).envelope_id as string)
        : undefined,
  };
}

// ─── Provisional / Off-Pulse path ─────────────────────────────────────

async function buildProvisionalResult(args: ContractBindArgs, reason: string): Promise<ContractResult> {
  // Phase 3 minimum: we don't have a local operator runtime yet (that's
  // Phase 6+ — 108 operators bundled as WASM). For now we emit a
  // status="provisional" envelope with no value, so the caller sees the
  // failure mode without the chat going red. Phase 6 wires the WASM
  // operators here and computes a real local result.
  const tick = ZeqondClock.now();
  const deviceKeyId = await getDeviceKeyId().catch(() => "unknown");
  const envelope = {
    kind: "provisional",
    operator_id: args.contractId,
    inputs: args.inputs,
    zeqond_local: tick.zeqond,
    phase_local: tick.phase,
    device_key_id: deviceKeyId,
    reason,
  };
  await signWithDeviceKey(JSON.stringify(envelope)).catch(() => "");
  return {
    value: NaN,
    unit: "",
    uncertainty: NaN,
    zeqProof: "",
    zeqond: tick.zeqond,
    phase: tick.phase,
    operatorChain: [args.contractId],
    status: "provisional",
    reason,
  };
}

// ─── verify() ──────────────────────────────────────────────────────────

export async function verify(args: {
  proof: string;
  operatorIds: string[];
  R_t: number;
  zeqond: number;
}): Promise<{ valid: boolean; protocol: string; reason?: string }> {
  const { base } = getSdkBase();
  try {
    const r = await fetch(`${base}/verify`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(args),
    });
    if (!r.ok) return { valid: false, protocol: "ZeqProof", reason: `HTTP ${r.status}` };
    const data = (await r.json()) as Record<string, unknown>;
    return {
      valid: data.valid === true,
      protocol: (data.protocol as string) || "ZeqProof",
    };
  } catch (err) {
    return { valid: false, protocol: "ZeqProof", reason: "network_unreachable" };
  }
}

// ─── history() ─────────────────────────────────────────────────────────

export async function history(args: { limit?: number } = {}): Promise<Array<{
  zeqond: number;
  transitionId: string;
  transitionType: string;
  proofDigest: string | null;
  envelope: Record<string, unknown> | null;
}>> {
  const limit = Math.min(Math.max(1, args.limit ?? 20), 200);
  const { kind } = getSdkBase();
  // Off-slug fallback — no chain for this caller until they're on a slug.
  if (kind !== "slug") return [];
  // Slug page: hit the chain history surface at /s/<slug>/sdk/chain/history.
  const m = /^\/s\/([^/]+)\//.exec(location.pathname);
  if (!m) return [];
  const slug = m[1];
  try {
    const r = await fetch(`/api/chain/${encodeURIComponent(slug)}/history?limit=${limit}`, {
      credentials: "same-origin",
    });
    if (!r.ok) return [];
    const data = (await r.json()) as { rows?: Array<Record<string, unknown>> };
    const rows = data.rows ?? [];
    return rows.map((row) => ({
      zeqond: typeof row.zeqond_number === "number" ? (row.zeqond_number as number) : Number(row.zeqond_number) || 0,
      transitionId: (row.transition_id as string) || "",
      transitionType: (row.transition_type as string) || "",
      proofDigest: (row.proof_digest as string) || null,
      envelope: (row.zsp_envelope as Record<string, unknown>) || null,
    }));
  } catch {
    return [];
  }
}

// ─── pending() — Off-Pulse queue introspection (Phase 3 stub) ──────────

export async function pending(): Promise<ContractResult[]> {
  // Phase 6+ will return entries from the OffPulseQueue. For now empty.
  return [];
}

// ─── Public surface ────────────────────────────────────────────────────

export const ZeqContract = {
  bind,
  verify,
  history,
  pending,
};

export default ZeqContract;

// Re-export device-key helpers for diagnostics.
export { getDeviceKeyId, getDevicePublicKeyJwk } from "./device-key.js";
